import React from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { LoadingType, setAccount, setLoadingState, setSignInStep, SignInStep } from 'actions';
import AccountForm, { AccountFormData } from 'components/Form/AccountForm/AccountForm';
import FormHeader from 'components/FormHeader';
import Loader from 'components/Loader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { identityFullStory } from 'utils/fullStory';
import BorrowerApi, { BorrowerDto } from 'api/BorrowerApi';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import AuthApi from 'api/AuthApi';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';

import styles from './CreateAccount.module.scss';

const CreateAccount = () => {
  const dispatch = useAppDispatch();
  const {
    firstName,
    lastName,
    email,
    phone,
    personalIdNumber,
    city,
    state: borrowerState,
    dateOfBirth,
    streetName,
    streetNumber,
    zip,
    county,
  } = useAppSelector((state) => state.borrower);

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const handleSubmit = async (data: AccountFormData) => {
    try {
      if ((email || data.email) && data.password && data.password === data.confirmPassword) {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const userEmail = (email || data.email)!;

        const borrowerApi = new BorrowerApi();
        const authApi = new AuthApi();

        const newBorrower = await borrowerApi.createBorrower({
          firstName: firstName,
          lastName: lastName,
          email: userEmail,
          personalIdNumber: personalIdNumber,
          phone: phone,
          address: {
            streetNumber: streetNumber,
            streetName: streetName,
            city: city,
            stateOrProvince: borrowerState,
            county: county,
            zipOrPostalCode: zip,
          },
          dateOfBirth: dateOfBirth,
        });

        if ((newBorrower as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
          dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));

          notificationManager.notifyFailure(FailureNotificationType.BorrowerEmailAlreadyExists);

          return;
        }

        const borrower = newBorrower as BorrowerDto;

        const tokens = await authApi.createAccount(userEmail, data.password, phone, borrower.id);

        localStorage.setItem('token', tokens!.accessToken);
        localStorage.setItem('refreshToken', tokens!.refreshToken);

        identityFullStory(email, email);

        dispatch(setAccount({ email: email }));
        dispatch(setSignInStep(SignInStep.EnterConfirmationCode));
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  const formMethods = useForm({
    defaultValues: {
      email: email,
      password: '',
      confirmPassword: '',
    },
  });

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <FormHeader title="Create Account" subtitle="" percent={25} />
          <FormProvider {...formMethods}>
            <AccountForm
              isEmailDisabled
              passwordLabel="Create Password"
              showEmailField
              showPasswordField
              showConfirmPasswordField
              showPasswordCaption
              buttonLabel="Submit"
              onSubmit={handleSubmit}
              buttonRowClassname={styles.buttonRow}
            />
          </FormProvider>
        </div>
      )}
    </>
  );
};

export default CreateAccount;
